import { dateSubstractor } from '@shared/utils/current-date-substractor/date-substractor';

import {
  calculateIsLessThanTenDaysLeftUntilLateFees,
  calculateIsLessThanThirtyDaysLeftUntilLateFees,
  calculateIsThirtyToTenDaysLeftUntilLateFees,
} from '../data/get-chips-data/utils/helpers';
import { type ApplicationDefinition } from '../types/application-definition';
import { type CustomerProfile } from '../types/customer-profile';
import {
  type MembershipApplicationDto,
  MembershipApplicationStatus,
} from '../types/membership-application';

export const getMembershipInformation = ({
  applicationDefinition,
  customerProfile,
  membershipApplication,
}: {
  applicationDefinition?: ApplicationDefinition;
  customerProfile: CustomerProfile;
  membershipApplication: MembershipApplicationDto | null;
  // eslint-disable-next-line complexity
}) => {
  const currentYearDate = new Date();

  const isApplicationSubmitted =
    membershipApplication?.status !== undefined &&
    membershipApplication.status === MembershipApplicationStatus.Submitted;

  const isApplicationUnfinished =
    membershipApplication?.status !== undefined &&
    membershipApplication.status !== MembershipApplicationStatus.Submitted &&
    membershipApplication.status !== MembershipApplicationStatus.PaymentPending;

  const isNewUser = !membershipApplication?.status && customerProfile?.totalMembership === 0;

  const isPreviousButNotCurrentMember =
    applicationDefinition !== undefined &&
    customerProfile.totalMembership !== null &&
    customerProfile.totalMembership > 0 &&
    !customerProfile.membershipYears?.includes(applicationDefinition.membershipYear);

  const isCurrentMember =
    applicationDefinition !== undefined &&
    customerProfile?.membershipYears?.includes(applicationDefinition.membershipYear);

  const isMemberAbleToUpgrade = Boolean(membershipApplication?.isEligibleForUpgrade);
  const isUpgrading = Boolean(membershipApplication?.isUpgrading);

  const isApplicationInReview =
    (membershipApplication?.orders !== undefined &&
      isApplicationSubmitted &&
      membershipApplication?.orders?.length > 0 &&
      membershipApplication?.orders?.some((order) => !order.isShipped)) ||
    membershipApplication?.status === MembershipApplicationStatus.PaymentPending;

  const subtractCurrentDateFromLateFeeStartDate =
    applicationDefinition &&
    Math.round(
      dateSubstractor(applicationDefinition.lateFeeStartDate, currentYearDate.toDateString())
    );

  const isLessThanThirtyDaysLeftUntilLateFees = Boolean(
    subtractCurrentDateFromLateFeeStartDate &&
    calculateIsLessThanThirtyDaysLeftUntilLateFees(subtractCurrentDateFromLateFeeStartDate)
  );

  const isThirtyToTenDaysLeftUntilLateFees = Boolean(
    subtractCurrentDateFromLateFeeStartDate &&
    calculateIsThirtyToTenDaysLeftUntilLateFees(subtractCurrentDateFromLateFeeStartDate)
  );

  const isLessThanTenDaysLeftUntilLateFees = Boolean(
    subtractCurrentDateFromLateFeeStartDate &&
    calculateIsLessThanTenDaysLeftUntilLateFees(subtractCurrentDateFromLateFeeStartDate)
  );

  return {
    isApplicationInReview,
    isApplicationUnfinished,
    isCurrentMember,
    isLessThanTenDaysLeftUntilLateFees,
    isLessThanThirtyDaysLeftUntilLateFees,
    isMemberAbleToUpgrade,
    isNewUser,
    isPreviousButNotCurrentMember,
    isThirtyToTenDaysLeftUntilLateFees,
    isUpgrading,
    subtractCurrentDateFromLateFeeStartDate,
  };
};
